import { useRef } from 'react';
import { parseJson } from '../../constants/propertyConstants';

const MB = 1024 * 1024;
const MAX_FILE_MB = 8;

const DOC_PRESETS = [
  'Sale deed',
  'Lease agreement',
  'NOC',
  'Layout plan',
  'Brochure',
  'KYC',
  'Payment receipt',
  'Other',
];

/**
 * Reusable Docs & Media upload — stores as JSON on Property:
 * documentsJson: [{ name, url }]   url = https… or data:image/…;base64,…
 * imagesJson:    [url, …]
 */
export default function PropertyDocsMediaUpload({
  documents = [],
  images = [],
  onDocumentsChange,
  onImagesChange,
  onToast,
}) {
  const docInputRef = useRef(null);
  const imgInputRef = useRef(null);
  const urlDocNameRef = useRef(null);
  const urlDocHrefRef = useRef(null);

  const notify = (severity, detail) => {
    if (onToast) onToast({ severity, summary: severity === 'error' ? 'Upload' : 'Docs', detail });
  };

  const readFileAsDataUrl = (file) =>
    new Promise((resolve, reject) => {
      if (file.size > MAX_FILE_MB * MB) {
        reject(new Error(`File too large (max ${MAX_FILE_MB} MB)`));
        return;
      }
      const r = new FileReader();
      r.onload = () => resolve(r.result);
      r.onerror = () => reject(new Error('Read failed'));
      r.readAsDataURL(file);
    });

  const addDocumentsFromFiles = async (files) => {
    const list = [...documents];
    for (const file of files) {
      try {
        const url = await readFileAsDataUrl(file);
        list.push({ name: file.name, url });
      } catch (e) {
        notify('error', e.message || 'Could not add file');
      }
    }
    onDocumentsChange(list);
  };

  const addImagesFromFiles = async (files) => {
    const list = [...images];
    for (const file of files) {
      if (!file.type.startsWith('image/')) {
        notify('warn', `${file.name} is not an image`);
        continue;
      }
      try {
        const url = await readFileAsDataUrl(file);
        list.push(url);
      } catch (e) {
        notify('error', e.message || 'Could not add image');
      }
    }
    onImagesChange(list);
  };

  const addUrlDocument = () => {
    const name = (urlDocNameRef.current?.value || '').trim() || 'External document';
    const url = (urlDocHrefRef.current?.value || '').trim();
    if (!url) {
      notify('warn', 'Paste a document URL (PDF link, Drive share link, etc.)');
      return;
    }
    onDocumentsChange([...documents, { name, url }]);
    urlDocHrefRef.current.value = '';
    urlDocNameRef.current.value = '';
    notify('success', 'Document link added');
  };

  const lbl = { fontSize: 11, fontWeight: 700, color: '#8898aa', textTransform: 'uppercase', marginBottom: 6 };
  const zone = {
    border: '2px dashed #c5cde0',
    borderRadius: 12,
    padding: '18px',
    textAlign: 'center',
    background: '#f8f9fe',
    cursor: 'pointer',
    transition: 'border-color .2s, background .2s',
  };

  return (
    <div>
      {/* Documents */}
      <div style={{ marginBottom: 22 }}>
        <p style={lbl}><i className="fas fa-file-alt" style={{ marginRight: 6, color: '#5e72e4' }} />Documents (PDF, scans — stored securely as data or link)</p>
        <div
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === 'Enter' && docInputRef.current?.click()}
          onClick={() => docInputRef.current?.click()}
          style={zone}
          onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#5e72e4'; e.currentTarget.style.background = '#f0f3ff'; }}
          onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#c5cde0'; e.currentTarget.style.background = '#f8f9fe'; }}
        >
          <i className="fas fa-cloud-upload-alt" style={{ fontSize: 28, color: '#5e72e4', marginBottom: 8 }} />
          <p style={{ margin: 0, fontWeight: 700, color: '#344767', fontSize: 14 }}>Click to upload documents</p>
          <p style={{ margin: '6px 0 0', fontSize: 12, color: '#8898aa' }}>PDF, images, Word — max {MAX_FILE_MB} MB each</p>
        </div>
        <input
          ref={docInputRef}
          type="file"
          multiple
          accept=".pdf,.doc,.docx,image/*,.txt"
          style={{ display: 'none' }}
          onChange={(e) => {
            const f = e.target.files;
            if (f?.length) addDocumentsFromFiles(Array.from(f));
            e.target.value = '';
          }}
        />

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10 }}>
          <span style={{ fontSize: 12, color: '#8898aa', alignSelf: 'center', marginRight: 4 }}>Quick label:</span>
          {DOC_PRESETS.map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => { if (urlDocNameRef.current) urlDocNameRef.current.value = p; }}
              style={{ padding: '4px 10px', borderRadius: 16, border: '1px solid #e3e6ec', background: '#fff', fontSize: 11, fontWeight: 600, color: '#525f7f', cursor: 'pointer' }}
            >
              {p}
            </button>
          ))}
        </div>

        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 12, alignItems: 'flex-end' }}>
          <div style={{ flex: '1 1 140px' }}>
            <label style={{ ...lbl, marginBottom: 4 }}>Document name</label>
            <input ref={urlDocNameRef} placeholder="e.g. Sale deed" style={{ width: '100%', padding: '10px 12px', borderRadius: 9, border: '1px solid #e3e6ec', boxSizing: 'border-box' }} />
          </div>
          <div style={{ flex: '2 1 220px' }}>
            <label style={{ ...lbl, marginBottom: 4 }}>Or paste URL</label>
            <input ref={urlDocHrefRef} placeholder="https://…" style={{ width: '100%', padding: '10px 12px', borderRadius: 9, border: '1px solid #e3e6ec', boxSizing: 'border-box' }} />
          </div>
          <button type="button" onClick={addUrlDocument}
            style={{ padding: '10px 18px', background: '#5e72e4', color: '#fff', border: 'none', borderRadius: 9, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>
            Add link
          </button>
        </div>

        {documents.length > 0 && (
          <ul style={{ listStyle: 'none', padding: 0, margin: '14px 0 0' }}>
            {documents.map((d, i) => (
              <li key={`${d.url}-${i}`} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', background: '#fff', borderRadius: 10, border: '1px solid #eef1f6', marginBottom: 8 }}>
                <i className="fas fa-file-pdf" style={{ color: '#f5365c', fontSize: 18 }} />
                <span style={{ flex: 1, fontWeight: 600, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.name}</span>
                <a href={d.url} target="_blank" rel="noreferrer" style={{ color: '#5e72e4', fontSize: 12, fontWeight: 600 }} onClick={(e) => e.stopPropagation()}>Preview</a>
                <button type="button" onClick={() => onDocumentsChange(documents.filter((_, j) => j !== i))}
                  style={{ background: '#f5365c18', color: '#f5365c', border: 'none', borderRadius: 8, padding: '6px 10px', cursor: 'pointer', fontWeight: 700 }}>
                  <i className="fas fa-times" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Photos / videos note — images only as data URLs for now */}
      <div>
        <p style={lbl}><i className="fas fa-images" style={{ marginRight: 6, color: '#2dce89' }} />Photos & floor plans</p>
        <div
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === 'Enter' && imgInputRef.current?.click()}
          onClick={() => imgInputRef.current?.click()}
          style={{ ...zone, borderColor: '#b8e6d3', background: '#f4fcf8' }}
          onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#2dce89'; }}
          onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#b8e6d3'; }}
        >
          <i className="fas fa-camera" style={{ fontSize: 28, color: '#2dce89', marginBottom: 8 }} />
          <p style={{ margin: 0, fontWeight: 700, color: '#344767', fontSize: 14 }}>Upload property photos</p>
          <p style={{ margin: '6px 0 0', fontSize: 12, color: '#8898aa' }}>JPG, PNG, WebP — shown on listing cards when wired</p>
        </div>
        <input
          ref={imgInputRef}
          type="file"
          multiple
          accept="image/*"
          style={{ display: 'none' }}
          onChange={(e) => {
            const f = e.target.files;
            if (f?.length) addImagesFromFiles(Array.from(f));
            e.target.value = '';
          }}
        />

        {images.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 14 }}>
            {images.map((src, i) => (
              <div key={`${src.slice(0, 40)}-${i}`} style={{ position: 'relative', width: 100, height: 100, borderRadius: 12, overflow: 'hidden', border: '1px solid #e3e6ec' }}>
                <img src={src} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                <button type="button" onClick={() => onImagesChange(images.filter((_, j) => j !== i))}
                  style={{ position: 'absolute', top: 4, right: 4, width: 26, height: 26, borderRadius: '50%', border: 'none', background: 'rgba(245,54,92,.9)', color: '#fff', cursor: 'pointer', fontSize: 12 }}>
                  <i className="fas fa-times" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/** Initialise arrays from property DTO strings */
export function docsMediaFromProperty(property) {
  return {
    documents: parseJson(property?.documentsJson, []),
    images: parseJson(property?.imagesJson, []),
  };
}
